import i18n, { type i18n as I18nInstance, type InitOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { SUPPORTED_LANGUAGE_CODES } from "@dosia/shared";
import { ADMIN_LOCALE_MESSAGES, ADMIN_BASE_LOCALE } from "./registry.js";

// Build i18next resources from the admin locale registry (single source).
const resources = Object.fromEntries(
  Object.entries(ADMIN_LOCALE_MESSAGES).map(([code, messages]) => [
    code,
    { translation: messages },
  ]),
);

// Safe as a plain boolean because init is synchronous (initImmediate: false).
let initialized = false;

/**
 * Initialize the admin-portal i18next singleton with the bundled `admin.*`
 * translations. Call once at app startup (main.tsx) and in the test setup.
 *
 * Mirrors the desktop `setupI18n` pattern (packages/shared) but on the admin
 * app's own bundle-local i18next instance with its own resources, so desktop's
 * 1016-key catalog is never touched. `initReactI18next` registers this instance
 * as the react-i18next default, so `useTranslation()` works without a Provider
 * (a `<I18nextProvider>` is still mounted in main.tsx for explicitness).
 *
 * fallbackLng = zh-Hans (现有中文即真相源). Keys are flat and dot-namespaced
 * ("admin.login.title"), so `keySeparator` is OFF — the whole string is the key.
 */
export function setupAdminI18n(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  plugins: any[] = [LanguageDetector, initReactI18next],
): I18nInstance {
  if (initialized) return i18n;

  let instance = i18n;
  for (const plugin of plugins) {
    instance = instance.use(plugin);
  }

  instance.init({
    resources,
    fallbackLng: ADMIN_BASE_LOCALE,
    supportedLngs: [...SUPPORTED_LANGUAGE_CODES],
    keySeparator: false,
    interpolation: { escapeValue: false },
    initImmediate: false, // synchronous init — resources are bundled inline
    detection: {
      // "navigator" intentionally excluded: 默认落 fallbackLng (zh-Hans),
      // 只有用户显式切换过语言（写入 localStorage）才尊重那次选择。
      order: ["localStorage"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
    },
  } as InitOptions);

  initialized = true;
  return i18n;
}

export { i18n };
